<template src="./add-new-keywords.component.html"></template>
<style scoped src="./add-new-keywords.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import PaginationComponent from '@/components/pagination/pagination.component.vue';
import api from '@/api';
import {
  countNonEmptyLines,
  getErrorMessage,
  getIsoDate,
  noop,
  Pagination,
  parseKeywordsToInsertInput,
  Sorting,
} from '@/helpers';
import {toasted} from '@/plugins/toasted';
import type {
  KeywordsToInsertBatchResponse,
  KeywordsToInsertBatchSummaryRow,
} from '@/models';

const MAX_KEYWORDS = 5000;
const BATCH_SUMMARY_PER_PAGE = 100;
const BATCH_SUMMARY_DEFAULT_COLUMN = 'created';
const BATCH_SUMMARY_DEFAULT_DIRECTION: 'asc' | 'desc' = 'desc';
const SORTABLE_BATCH_COLUMNS = [
  'status',
  'created',
  'totalKeywords',
  'keywordsWithOneImage',
  'keywordsWithVisymoArticles',
  'maskedKeywords',
] as const;
type BatchSortColumn = typeof SORTABLE_BATCH_COLUMNS[number];

function getParsedInput(value: string) {
  return parseKeywordsToInsertInput(value, MAX_KEYWORDS);
}

export default Vue.extend({
  components: {
    PaginationComponent,
    Sidebar,
  },
  data() {
    return {
      form: {
        keywords: '',
      },
      isSubmitting: false,
      submitError: '',
      result: null as KeywordsToInsertBatchResponse | null,
      ac: null as AbortController | null,
      batchRows: [] as KeywordsToInsertBatchSummaryRow[],
      showBatchLoading: false,
      batchTableError: '',
      sorting: new Sorting({
        column: BATCH_SUMMARY_DEFAULT_COLUMN,
        direction: BATCH_SUMMARY_DEFAULT_DIRECTION,
      }),
      pagination: new Pagination({
        perPage: BATCH_SUMMARY_PER_PAGE,
      }),
    };
  },
  computed: {
    keywordRows() {
      const min = 8;
      const max = 18;
      const nonEmptyLineCount = countNonEmptyLines(this.form.keywords);
      return Math.min(Math.max(nonEmptyLineCount || 1, min), max);
    },
    nonEmptyLineCount() {
      return countNonEmptyLines(this.form.keywords);
    },
    parsedInput() {
      return getParsedInput(this.form.keywords);
    },
    validationError() {
      if (!this.form.keywords.trim()) return '';
      return getParsedInput(this.form.keywords).errorMessage ?? '';
    },
    displayedError() {
      if (!this.form.keywords.trim()) return this.submitError;
      return getParsedInput(this.form.keywords).errorMessage ?? this.submitError;
    },
    isFormValid() {
      const parsedInput = getParsedInput(this.form.keywords);
      return !this.isSubmitting && parsedInput.items.length > 0 && !parsedInput.errorMessage;
    },
    formatExamples() {
      return [
        'running shoes,us',
        'credit card;it',
      ];
    },
    batchSortColumn(): BatchSortColumn {
      const fallback = BATCH_SUMMARY_DEFAULT_COLUMN;
      const column = this.sorting.column;
      if (SORTABLE_BATCH_COLUMNS.includes(column as BatchSortColumn)) {
        return column as BatchSortColumn;
      }
      return fallback;
    },
    batchSortDirection(): 'asc' | 'desc' {
      if (this.sorting.column !== this.batchSortColumn) {
        return BATCH_SUMMARY_DEFAULT_DIRECTION;
      }
      return this.sorting.direction === 'asc' ? 'asc' : BATCH_SUMMARY_DEFAULT_DIRECTION;
    },
  },
  watch: {
    '$route.query': {
      async handler(query: Record<string, any>) {
        this.sorting.loadFromQuery(query);
        this.pagination.loadFromQuery(query);
        this.pagination.perPage = BATCH_SUMMARY_PER_PAGE;
        await this.getBatchSummaries();
      },
      immediate: true,
    },
    'form.keywords'(value: string) {
      this.submitError = '';
      if (value.trim()) this.result = null;
    },
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  methods: {
    async onSubmit() {
      this.submitError = '';
      const parsedInput = getParsedInput(this.form.keywords);
      const validationError = this.form.keywords.trim()
        ? parsedInput.errorMessage ?? ''
        : '';
      const isFormValid = !this.isSubmitting && parsedInput.items.length > 0 && !parsedInput.errorMessage;

      if (!isFormValid) {
        if (validationError) toasted.showErrorMessage(validationError);
        return;
      }

      this.isSubmitting = true;
      try {
        const response = await api.execute('post', '/keywords-to-insert/batches', {
          items: parsedInput.items,
        }) as KeywordsToInsertBatchResponse;

        this.form.keywords = '';
        this.result = response;
        toasted.showSuccessMessage(`${response.insertedCount} keywords added successfully.`);
        await this.refreshBatchSummariesAfterSubmit();
      } catch (e) {
        console.error(e);
        this.submitError = getErrorMessage(e);
        toasted.showErrorMessage(this.submitError);
      } finally {
        this.isSubmitting = false;
      }
    },
    async refreshBatchSummariesAfterSubmit() {
      const query = {
        page: 1,
        perPage: BATCH_SUMMARY_PER_PAGE,
        column: this.batchSortColumn,
        direction: this.batchSortDirection,
      };
      const pageInRoute = parseInt(String(this.$route.query.page ?? ''), 10);
      const routePage = Number.isNaN(pageInRoute) ? 1 : pageInRoute;
      const routePerPage = parseInt(String(this.$route.query.perPage ?? ''), 10);
      const routeColumn = String(this.$route.query.column || BATCH_SUMMARY_DEFAULT_COLUMN);
      const routeDirection = String(this.$route.query.direction || BATCH_SUMMARY_DEFAULT_DIRECTION);

      if (
        routePage === 1 &&
        routePerPage === BATCH_SUMMARY_PER_PAGE &&
        routeColumn === query.column &&
        routeDirection === query.direction
      ) {
        await this.getBatchSummaries();
        return;
      }
      this.updateQueryParams(query);
    },
    async getBatchSummaries() {
      this.showBatchLoading = true;
      this.batchTableError = '';
      try {
        this.ac?.abort();
        this.ac = new AbortController();
        const searchParams = this.getBatchSummarySearchParams();
        const {rows, count} = await api.execute(
          'get',
          `/keywords-to-insert/batches/summary?${searchParams.toString()}`,
          null,
          null,
          this.ac.signal,
        ) as {rows: KeywordsToInsertBatchSummaryRow[], count: number};
        this.batchRows = rows;
        this.pagination.total = count;
      } catch (e) {
        console.error(e);
        if (!axios.isCancel(e)) {
          this.batchTableError = getErrorMessage(e);
          toasted.showErrorMessage(this.batchTableError);
        }
      } finally {
        this.showBatchLoading = false;
      }
    },
    getBatchSummarySearchParams() {
      const searchParams = new URLSearchParams();
      searchParams.append('page', String(this.pagination.page || 1));
      searchParams.append('perPage', String(BATCH_SUMMARY_PER_PAGE));
      searchParams.append('column', this.batchSortColumn);
      searchParams.append('direction', this.batchSortDirection);
      return searchParams;
    },
    onSort(column: BatchSortColumn) {
      const direction = this.batchSortColumn === column && this.batchSortDirection === 'desc'
        ? 'asc'
        : 'desc';
      this.updateQueryParams({
        page: 1,
        perPage: BATCH_SUMMARY_PER_PAGE,
        column,
        direction,
      });
    },
    updatePage(page: number) {
      this.updateQueryParams({
        page,
        perPage: BATCH_SUMMARY_PER_PAGE,
        column: this.batchSortColumn,
        direction: this.batchSortDirection,
      });
    },
    updateQueryParams(query: {[key: string]: any}) {
      this.$router.push({query}).catch(noop);
    },
    getSortIcon(column: BatchSortColumn) {
      if (this.batchSortColumn !== column) return '';
      return this.batchSortDirection === 'asc' ? '▲' : '▼';
    },
    formatBatchCreated(value: string | Date) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const isoDate = getIsoDate(date);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${isoDate} ${hours}:${minutes}`;
    },
  },
});
</script>
